import { create } from 'zustand'
import {
  detectLanguage,
  offeneBestaetigung,
  type Channel,
  type Session,
  type Showcase,
  type SourceRef,
  type ToolCallRecord,
  type TourStop,
} from '@k4ia/shared'
import {
  fetchSessionState,
  fetchShowcases,
  fetchTour,
  initSession,
  postChatMessage,
  postFeedback,
  postToolConfirmation,
  type ChatEntry,
} from '../lib/api'
import { streamChatEvents } from '../lib/sse'
import { LEERER_KONTAKT, type ContactData } from '../components/ContactCard'
import {
  buildSuggestions,
  chipKind,
  nextShowcaseId,
  showcaseOfAnswer,
  tourOrder,
  type TourState,
} from '../lib/tour'
import { STRINGS, type UiLocale } from '../lib/i18n-strings'

export type AvatarState = 'idle' | 'listening' | 'thinking' | 'speaking' | 'error'

export interface UiMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  sources: SourceRef[]
  toolCalls: ToolCallRecord[]
  pendingConfirmation?: { toolCallId: string; name: string; input: Record<string, unknown> }
  /**
   * Der Showcase, um den es in dieser Nachricht geht: bei Fragen der Kontext
   * (Tag, Kamera, Rundgang), bei Antworten der aus `get_showcase`. Die Blase
   * zeigt dazu den Kopf mit „Mehr dazu" und „Ans Team weitergeben".
   */
  showcaseId?: string
  /** Die Karte wurde abgeschickt und wartet auf die Antwort — Knopf aus, kein zweites Abschicken. */
  confirming?: boolean
  /** Der Server hat die Eingaben der Karte abgelehnt; die Karte bleibt offen und zeigt das. */
  confirmError?: string
  streaming?: boolean
  /** set once the visitor rated this answer, so the UI can show which thumb was picked */
  rating?: 'up' | 'down'
}

interface ChatStore {
  session: Session | null
  messages: UiMessage[]
  avatarState: AvatarState
  /** Lautstärke des Mikrofons, 0…1 — nur für die Anzeige, dass gerade zugehört wird. */
  micLevel: number
  suggestions: string[]
  consent: { ai: boolean; microphone: boolean; camera: boolean } | null
  errorMessage: string | null
  ready: boolean

  setConsent: (consent: { ai: boolean; microphone: boolean; camera: boolean }) => void
  /**
   * Sprache der Oberfläche (lib/i18n.ts): vom Browser vorgegeben, danach von
   * dem, was der Besucher schreibt oder spricht. `noteLanguage` prüft einen
   * Text und wechselt nur bei klarem Befund.
   */
  locale: UiLocale
  setLocale: (locale: UiLocale) => void
  noteLanguage: (text: string) => void
  init: (channel: Channel, showcaseContextId?: string) => Promise<void>
  /**
   * Hinweis beim Öffnen (Flo, 22.10.): `fortgesetzt` — der Besucher landet in
   * seinem Gespräch von vorhin und kann weitermachen oder neu anfangen;
   * `neu` — das alte war abgelaufen, der Chat fängt leer an. `am` ist die
   * letzte Nachricht des vorigen Gesprächs.
   */
  sitzungsHinweis: { art: 'fortgesetzt' | 'neu'; am: string | null } | null
  hinweisSchliessen: () => void
  /** „Neues Gespräch“: neue Sitzung beim Worker, leerer Chat; das alte bleibt in der Datenbank. */
  neuesGespraech: () => Promise<void>
  sendMessage: (
    text: string,
    showcaseContextId?: string,
    imageDataUrl?: string,
    /** Mehrere Showcases im Kamerabild (ganze Vitrine): K4ia sagt, was zu sehen ist, und fragt nach. */
    showcaseContextIds?: string[],
    /** NFC-Tag gescannt bzw. Zug im geführten Rundgang. */
    entry?: ChatEntry,
  ) => Promise<void>
  /**
   * Katalog für Chips und Köpfe: die Showcases (mit FAQ) und der Rundgang aus
   * dem Admin. Einmal je Seite geladen; ohne ihn gibt es keine Vorschläge.
   */
  showcases: Showcase[]
  tourStops: TourStop[]
  loadCatalog: () => Promise<void>
  /** Geführter Rundgang, solange er läuft (lib/tour.ts). */
  tour: TourState | null
  startTour: () => Promise<void>
  tourArrived: () => Promise<void>
  tourNext: () => Promise<void>
  tourEnd: () => Promise<void>
  /** Chip getippt: Rundgang-Chips steuern den Rundgang, alle anderen sind eine Frage. */
  pickSuggestion: (text: string) => Promise<void>
  /** „Mehr dazu" und „Ans Team weitergeben" aus dem Kopf einer Antwort. */
  askMore: (showcaseId: string) => Promise<void>
  askFollowup: (showcaseId: string) => Promise<void>
  /**
   * Einstieg über den NFC-Tag am Stand (SPEC „NFC-Konzept"): K4ia erklärt den
   * Showcase sofort — im Chat, oder gesprochen: dann startet der Sprachmodus
   * von selbst (`voiceAutoStart`), und sobald er verbunden ist, geht die
   * Markierung `[[tag:<id>]]` an die Brücke (`pendingVoiceEntry`).
   */
  beginShowcaseEntry: (
    showcase: { id: string; title: string },
    mode: 'chat' | 'voice',
  ) => Promise<void>
  pendingVoiceEntry: { id: string; title: string } | null
  /** Der Sprachmodus soll starten, ohne dass jemand den Knopf drückt (Einstieg per Tag). */
  voiceAutoStart: boolean
  setVoiceAutoStart: (an: boolean) => void
  /** Sprachmodus verbunden: Einstieg gesprochen. Sprachmodus gescheitert: Einstieg im Chat. */
  flushVoiceEntry: (weg: 'voice' | 'chat') => Promise<void>
  /**
   * Bestätigt oder lehnt ein Werkzeug mit Außenwirkung ab. `input` sind die
   * Eingaben aus einer Karte (Kontaktkarte): Sie ersetzen die Felder des
   * Aufrufs. Läuft ein Sprachgespräch, spricht K4ia das Ergebnis danach.
   */
  confirmTool: (
    toolCallId: string,
    confirmed: boolean,
    input?: Record<string, unknown>,
  ) => Promise<void>
  /**
   * Was der Besucher zuletzt in die Kontaktkarte getippt hat — nur im
   * Speicher dieser Seite, nie persistiert. Die zweite Rückfrage im selben
   * Besuch bekommt die Karte damit schon ausgefüllt.
   */
  lastContact: ContactData
  /**
   * Angefangene Kontaktkarten, je Aufruf: Die Karte kann aus dem Bild wandern
   * (Sprachmodus zeigt nur die letzten Beiträge) und wieder auftauchen — dann
   * steht noch drin, was der Besucher getippt hat. Nur im Speicher der Seite.
   */
  kartenEntwuerfe: Record<string, ContactData>
  merkeKartenEntwurf: (toolCallId: string, data: ContactData) => void
  /**
   * Sprachmodus: Ob die letzte Antwort im Server-Verlauf ein Werkzeug offen
   * hat, das der Besucher bestätigen muss (die Kontaktkarte). Das Transkript
   * über ElevenLabs trägt keine Werkzeugaufrufe — deshalb nachsehen und die
   * Karte an die letzte Sprechblase hängen. Gibt zurück, ob etwas offen ist.
   */
  syncPendingConfirmation: () => Promise<boolean>
  /** Nach jedem Zug: Vorschläge neu berechnen (aus Rundgang, Showcase, FAQ). */
  refreshSuggestions: () => void
  rateMessage: (messageId: string, rating: 'up' | 'down') => Promise<void>
  /** Kiosk mode: wipe the screen so the next visitor starts clean (see pages/KioskView.tsx). */
  reset: () => void
  /** Show mode: re-read the session so the big screen picks up what the tablet sent. */
  refresh: () => Promise<void>

  // Voice mode (Phase 2, ADR 0004) writes into the same transcript/avatar
  // state as text chat, per FR-10/FR-23 (cross-channel continuity, parallel
  // transcript) — see hooks/useVoiceSession.ts for the ElevenLabs wiring.
  setAvatarState: (state: AvatarState) => void
  setMicLevel: (level: number) => void
  /** Sprachmodus: Das Mikrofon ist geöffnet (Tippen an) — schließt sich, wenn K4ia antwortet. */
  micOpen: boolean
  setMicOpen: (open: boolean) => void
  /**
   * Sprachmodus: Während K4ia spricht, hat das Gerät gerade eine Stimme
   * gehört — sie wird leiser und prüft, ob jemand dazwischenredet
   * (DazwischenAblauf). Die Anzeige sagt dann „Ich höre dich …“.
   */
  hoertDich: boolean
  setHoertDich: (hoertDich: boolean) => void
  /**
   * Gesetzt, solange ein Sprachgespräch läuft: schickt eine Markierung wie
   * `[[kamera:<id>]]` oder `[[foto]]` als Textnachricht über ElevenLabs, die
   * Brücke übersetzt sie, und K4ia *spricht* die Antwort. `null` heißt: kein
   * Sprachgespräch — Kamera und Foto antworten nur im Chat.
   */
  voiceMarkerSender: ((marker: string) => void) | null
  setVoiceMarkerSender: (sender: ((marker: string) => void) | null) => void
  /** `erkennen` false für erzeugte Zugtexte („Ich stehe vor: …"), die nichts über die Sprache des Besuchers sagen. */
  addUserTranscript: (text: string, erkennen?: boolean) => void
  beginVoiceTurn: () => string
  appendVoiceText: (id: string, delta: string) => void
  /** Unterbrochen: Die Blase zeigt nur, was gesprochen wurde, mit „…" am Ende. */
  truncateVoiceText: (id: string, spoken: string) => void
  finalizeVoiceTurn: (id: string) => void
  /**
   * Entfernt eine Nachricht wieder — für den Platzhalter mit der fliegenden
   * Figur, der zeigt, dass K4ia gerade ein Bild ansieht. Sobald die echte
   * Antwort beginnt (Chat oder Sprache), weicht er ihr.
   */
  dropMessage: (id: string) => void
}

export const useChatStore = create<ChatStore>((set, get) => ({
  session: null,
  messages: [],
  avatarState: 'idle',
  micLevel: 0,
  suggestions: [],
  consent: null,
  errorMessage: null,
  ready: false,

  setConsent: (consent) => set({ consent }),

  locale: 'de',
  setLocale: (locale) => {
    if (locale === get().locale) return
    set({ locale })
    get().refreshSuggestions()
  },
  noteLanguage: (text) => {
    const erkannt = detectLanguage(text)
    if (erkannt) get().setLocale(erkannt)
  },

  showcases: [],
  tourStops: [],
  tour: null,
  loadCatalog: async () => {
    // Beides darf fehlen (Netz weg, Rundgang leer) — dann gibt es keine
    // Chips, aber das Gespräch läuft.
    const [sc, tour] = await Promise.all([
      fetchShowcases().catch(() => ({ showcases: [] as Showcase[] })),
      fetchTour().catch(() => ({ stops: [] as TourStop[] })),
    ])
    set({ showcases: sc.showcases, tourStops: tour.stops })
    get().refreshSuggestions()
  },

  startTour: async () => {
    const ids = tourOrder(get().tourStops, get().showcases)
    if (ids.length === 0) {
      await get().sendMessage(STRINGS[get().locale].tourFallback)
      return
    }
    set({ tour: { ids, index: 0, phase: 'walk' } })
    await sendeRundgangZug(get, ids[0]!, 'tour-walk')
  },
  tourArrived: async () => {
    const tour = get().tour
    if (!tour) return
    const id = tour.ids[tour.index]
    if (!id) return
    set({ tour: { ...tour, phase: 'arrived' } })
    await sendeRundgangZug(get, id, 'tour-arrived')
  },
  tourNext: async () => {
    const tour = get().tour
    if (!tour) return
    const naechste = tour.ids[tour.index + 1]
    if (!naechste) {
      await get().tourEnd()
      return
    }
    set({ tour: { ...tour, index: tour.index + 1, phase: 'walk' } })
    await sendeRundgangZug(get, naechste, 'tour-walk')
  },
  tourEnd: async () => {
    set({ tour: null })
    await sendeRundgangZug(get, null, 'tour-end')
  },
  pickSuggestion: async (text) => {
    switch (chipKind(text)) {
      case 'here':
        return get().tourArrived()
      case 'next':
        return get().tourNext()
      case 'end':
        return get().tourEnd()
      case 'tour':
        return get().startTour()
      case 'nextShowcase': {
        // Vom zuletzt besprochenen Showcase zum nächsten in der Reihenfolge des
        // Rundgangs — und ab da läuft der Rundgang, mit Weg und Erklärung.
        const letzter = letzterShowcase(get().messages)
        const ids = tourOrder(get().tourStops, get().showcases)
        const naechste = letzter ? nextShowcaseId(letzter, ids) : (ids[0] ?? null)
        if (!naechste) return
        set({ tour: { ids, index: ids.indexOf(naechste), phase: 'walk' } })
        return sendeRundgangZug(get, naechste, 'tour-walk')
      }
      default:
        return get().sendMessage(text)
    }
  },
  askMore: async (showcaseId) => {
    const sc = get().showcases.find((s) => s.id === showcaseId)
    await get().sendMessage(STRINGS[get().locale].askMore(sc?.title ?? 'Showcase'), showcaseId)
  },
  askFollowup: async (showcaseId) => {
    const sc = get().showcases.find((s) => s.id === showcaseId)
    await get().sendMessage(STRINGS[get().locale].askFollowup(sc?.title ?? 'Showcase'), showcaseId)
  },
  refreshSuggestions: () => {
    const s = get()
    set({
      suggestions: buildSuggestions({
        tour: s.tour,
        showcaseId: letzterShowcase(s.messages),
        showcases: s.showcases,
        order: tourOrder(s.tourStops, s.showcases),
        asked: s.messages.filter((m) => m.role === 'user').map((m) => m.content),
        locale: s.locale,
      }),
    })
  },

  sitzungsHinweis: null,
  hinweisSchliessen: () => set({ sitzungsHinweis: null }),
  neuesGespraech: async () => {
    const channel = get().session?.channel ?? 'web'
    await initSession({ channel, locale: get().locale, neu: true })
    const state = await fetchSessionState()
    set({
      session: state.session,
      messages: [],
      suggestions: [],
      tour: null,
      avatarState: 'idle',
      errorMessage: null,
      sitzungsHinweis: null,
      kartenEntwuerfe: {},
    })
    get().refreshSuggestions()
  },

  init: async (channel, showcaseContextId) => {
    const start = await initSession({ channel, locale: get().locale })
    const state = await fetchSessionState()
    // Nur am Handy (Web) und nur, wenn es etwas zu sagen gibt: Ein Gespräch,
    // das nach einer Pause (Minuten aus dem Admin) weitergeht, oder ein abgelaufenes.
    const am = start.neuBegonnen?.letzteNachrichtAm ?? start.fortgesetzt?.letzteNachrichtAm ?? null
    const herMs = am ? Date.now() - Date.parse(am) : 0
    const hinweisAbMs = (start.hinweisAbMinuten ?? 15) * 60_000
    const sitzungsHinweis =
      channel !== 'web' || !am
        ? null
        : start.neuBegonnen?.grund === 'timeout'
          ? { art: 'neu' as const, am }
          : start.fortgesetzt && state.messages.length > 0 && herMs >= hinweisAbMs
            ? { art: 'fortgesetzt' as const, am }
            : null
    set({
      session: state.session,
      ready: true,
      sitzungsHinweis,
      messages: state.messages
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => ({
          id: m.id,
          role: m.role as 'user' | 'assistant',
          content: m.content,
          sources: m.sources,
          toolCalls: m.toolCalls,
          pendingConfirmation: findPendingConfirmation(m.toolCalls),
        })),
    })
    if (showcaseContextId && get().messages.length === 0) {
      await get().sendMessage('__greet__', showcaseContextId)
    }
  },

  beginShowcaseEntry: async (showcase, mode) => {
    if (mode === 'chat') {
      await get().sendMessage(
        `${STRINGS[get().locale].tourArrived} ${showcase.title}`,
        showcase.id,
        undefined,
        undefined,
        'tag',
      )
      return
    }
    set({ pendingVoiceEntry: { id: showcase.id, title: showcase.title }, voiceAutoStart: true })
  },
  pendingVoiceEntry: null,
  voiceAutoStart: false,
  setVoiceAutoStart: (voiceAutoStart) => set({ voiceAutoStart }),
  flushVoiceEntry: async (weg) => {
    const eintrag = get().pendingVoiceEntry
    if (!eintrag) return
    set({ pendingVoiceEntry: null, voiceAutoStart: false })
    const text = `${STRINGS[get().locale].tourArrived} ${eintrag.title}`
    const sender = get().voiceMarkerSender
    if (weg === 'voice' && sender) {
      get().addUserTranscript(text, false)
      sender(`[[tag:${eintrag.id}]]`)
      return
    }
    await get().sendMessage(text, eintrag.id, undefined, undefined, 'tag')
  },

  sendMessage: async (text, showcaseContextId, imageDataUrl, showcaseContextIds, entry) => {
    const isGreeting = text === '__greet__'
    const userMsgId = crypto.randomUUID()
    const assistantMsgId = crypto.randomUUID()
    if (!isGreeting && !entry) get().noteLanguage(text)

    set((s) => ({
      messages: isGreeting
        ? s.messages
        : [
            ...s.messages,
            {
              id: userMsgId,
              role: 'user',
              content: text,
              sources: [],
              toolCalls: [],
              showcaseId: showcaseContextId,
            },
          ],
      avatarState: 'thinking',
      errorMessage: null,
    }))

    try {
      const response = await postChatMessage({
        message: isGreeting ? 'Hallo! Was kannst du mir zeigen?' : text,
        showcaseContextId,
        imageDataUrl,
        showcaseContextIds,
        entry,
      })

      if (!response.ok) {
        const body = await response.json().catch(() => ({ message: 'Unbekannter Fehler' }))
        set({
          avatarState: 'error',
          errorMessage: (body as { message?: string }).message ?? 'Fehler beim Senden.',
        })
        return
      }

      set((s) => ({
        messages: [
          ...s.messages,
          {
            id: assistantMsgId,
            role: 'assistant',
            content: '',
            sources: [],
            toolCalls: [],
            streaming: true,
          },
        ],
      }))

      for await (const event of streamChatEvents(response)) {
        if (event.type === 'token') {
          set((s) => ({
            messages: s.messages.map((m) =>
              m.id === assistantMsgId ? { ...m, content: m.content + event.delta } : m,
            ),
          }))
        } else if (event.type === 'source') {
          set((s) => ({
            messages: s.messages.map((m) =>
              m.id === assistantMsgId ? { ...m, sources: [...m.sources, event.source] } : m,
            ),
          }))
        } else if (event.type === 'suggestion') {
          set({ suggestions: event.suggestions })
        } else if (event.type === 'tool_call') {
          set((s) => ({
            messages: s.messages.map((m) =>
              m.id === assistantMsgId
                ? {
                    ...m,
                    toolCalls: [
                      ...m.toolCalls,
                      {
                        id: event.id,
                        name: event.name,
                        input: event.input,
                        requiresConfirmation: event.requiresConfirmation,
                      },
                    ],
                    pendingConfirmation: event.requiresConfirmation
                      ? { toolCallId: event.id, name: event.name, input: event.input }
                      : m.pendingConfirmation,
                  }
                : m,
            ),
          }))
        } else if (event.type === 'tool_result') {
          // Das Ergebnis an den Aufruf hängen: show_media bringt darin die
          // Adresse des Bildes, das die Blase zeigt (MediaCard).
          set((s) => ({
            messages: s.messages.map((m) =>
              m.id === assistantMsgId
                ? {
                    ...m,
                    toolCalls: m.toolCalls.map((t) =>
                      t.id === event.id ? { ...t, output: event.output } : t,
                    ),
                  }
                : m,
            ),
          }))
        } else if (event.type === 'done') {
          // Adopt the server's canonical message id so a rating can reference
          // the row that was persisted for this answer (see routes/feedback.ts).
          set((s) => ({
            avatarState: 'idle',
            messages: s.messages.map((m) =>
              m.id === assistantMsgId
                ? {
                    ...m,
                    id: event.messageId,
                    streaming: false,
                    showcaseId: showcaseOfAnswer(event.toolCalls, s.showcases, showcaseContextId, [
                      m.content,
                      text,
                    ]),
                  }
                : m,
            ),
          }))
          get().refreshSuggestions()
        } else if (event.type === 'error') {
          set({ avatarState: 'error', errorMessage: event.message })
        }
      }
    } catch (err) {
      set({
        avatarState: 'error',
        errorMessage: err instanceof Error ? err.message : 'Verbindung unterbrochen.',
      })
    }
  },

  lastContact: LEERER_KONTAKT,
  kartenEntwuerfe: {},
  merkeKartenEntwurf: (toolCallId, data) =>
    set((s) => ({ kartenEntwuerfe: { ...s.kartenEntwuerfe, [toolCallId]: data } })),

  confirmTool: async (toolCallId, confirmed, input) => {
    set((s) => ({
      avatarState: 'thinking',
      messages: s.messages.map((m) =>
        m.pendingConfirmation?.toolCallId === toolCallId
          ? { ...m, confirming: true, confirmError: undefined }
          : m,
      ),
    }))
    const assistantMsgId = crypto.randomUUID()
    try {
      const response = await postToolConfirmation(toolCallId, confirmed, input)
      if (!response.ok) {
        // Karte bleibt offen — der Besucher sieht, dass es nicht ging, und
        // kann korrigieren, statt seine Eingaben zu verlieren.
        const body = (await response.json().catch(() => ({}))) as { error?: string }
        set((s) => ({
          avatarState: 'idle',
          messages: s.messages.map((m) =>
            m.pendingConfirmation?.toolCallId === toolCallId
              ? {
                  ...m,
                  confirming: false,
                  confirmError:
                    body.error ?? 'Das hat gerade nicht geklappt. Bitte prüf deine Angaben.',
                }
              : m,
          ),
        }))
        return
      }
      if (confirmed && input) {
        set({ lastContact: { ...get().lastContact, ...kontaktAus(input) } })
      }
      // Abgeschickt oder geschlossen: Der Entwurf hat seinen Zweck erfüllt.
      set((s) => {
        const { [toolCallId]: _weg, ...rest } = s.kartenEntwuerfe
        return { kartenEntwuerfe: rest }
      })
      set((s) => ({
        messages: [
          ...s.messages.map((m) =>
            m.pendingConfirmation?.toolCallId === toolCallId
              ? { ...m, pendingConfirmation: undefined, confirming: false }
              : m,
          ),
          {
            id: assistantMsgId,
            role: 'assistant',
            content: '',
            sources: [],
            toolCalls: [],
            streaming: true,
          },
        ],
      }))
      for await (const event of streamChatEvents(response)) {
        if (event.type === 'token') {
          set((s) => ({
            messages: s.messages.map((m) =>
              m.id === assistantMsgId ? { ...m, content: m.content + event.delta } : m,
            ),
          }))
        } else if (event.type === 'done') {
          set((s) => ({
            avatarState: 'idle',
            messages: s.messages.map((m) =>
              m.id === assistantMsgId ? { ...m, streaming: false } : m,
            ),
          }))
          get().refreshSuggestions()
        }
      }
      // Sprachgespräch: Die Antwort steht jetzt im Verlauf; K4ia soll sie
      // sprechen. Die getippte Blase weicht der gesprochenen — sonst stünde
      // dieselbe Aussage zweimal untereinander.
      const sender = get().voiceMarkerSender
      if (sender && confirmed && input) {
        get().dropMessage(assistantMsgId)
        sender('[[karte]]')
      }
    } catch (err) {
      set({
        avatarState: 'error',
        errorMessage: err instanceof Error ? err.message : 'Verbindung unterbrochen.',
      })
    }
  },

  setAvatarState: (avatarState) => set({ avatarState }),
  setMicLevel: (micLevel) => set({ micLevel }),
  micOpen: false,
  setMicOpen: (micOpen) => set({ micOpen }),
  hoertDich: false,
  setHoertDich: (hoertDich) => set({ hoertDich }),
  voiceMarkerSender: null,
  setVoiceMarkerSender: (voiceMarkerSender) => set({ voiceMarkerSender }),

  addUserTranscript: (text, erkennen = true) => {
    set((s) => ({
      messages: [
        ...s.messages,
        { id: crypto.randomUUID(), role: 'user', content: text, sources: [], toolCalls: [] },
      ],
    }))
    if (erkennen) get().noteLanguage(text)
  },

  beginVoiceTurn: () => {
    const id = crypto.randomUUID()
    set((s) => ({
      messages: [
        ...s.messages,
        { id, role: 'assistant', content: '', sources: [], toolCalls: [], streaming: true },
      ],
    }))
    return id
  },

  appendVoiceText: (id, delta) =>
    set((s) => ({
      messages: s.messages.map((m) => (m.id === id ? { ...m, content: m.content + delta } : m)),
    })),

  truncateVoiceText: (id, spoken) =>
    set((s) => ({
      messages: s.messages.map((m) => {
        if (m.id !== id) return m
        const kurz = spoken.trim()
        if (!kurz || kurz.length >= m.content.trim().length) return m
        return { ...m, content: `${kurz.replace(/[\s.,;:!?…–—-]+$/u, '')} …` }
      }),
    })),

  dropMessage: (id) => set((s) => ({ messages: s.messages.filter((m) => m.id !== id) })),

  finalizeVoiceTurn: (id) =>
    set((s) => ({
      messages: s.messages.map((m) => (m.id === id ? { ...m, streaming: false } : m)),
    })),

  syncPendingConfirmation: async () => {
    const state = await fetchSessionState()
    const letzte = [...state.messages].reverse().find((m) => m.role === 'assistant')
    // Die jüngste offene Bestätigung im ganzen Verlauf: Die Karte bleibt
    // offen, wenn der Besucher zwischendurch weiterredet. Hängt sie schon an
    // einer Blase (`schonDa`), bleibt sie dort.
    const offen = offeneBestaetigung(state.messages)
    const pending = offen
      ? { toolCallId: offen.id, name: offen.name, input: offen.input }
      : undefined
    // Der Showcase der gesprochenen Antwort (für Kopf und Chips) kommt aus
    // denselben Werkzeugaufrufen — das Transkript allein kennt ihn nicht.
    const showcaseId = letzte
      ? showcaseOfAnswer(letzte.toolCalls, get().showcases, letzterShowcase(get().messages), [
          letzte.content,
        ])
      : undefined
    set((s) => {
      const idx = s.messages.map((m) => m.role).lastIndexOf('assistant')
      if (idx < 0) return s
      const schonDa = s.messages.some(
        (m) => pending && m.pendingConfirmation?.toolCallId === pending.toolCallId,
      )
      return {
        messages: s.messages.map((m, i) =>
          i === idx
            ? {
                ...m,
                showcaseId: showcaseId ?? m.showcaseId,
                pendingConfirmation: pending && !schonDa ? pending : m.pendingConfirmation,
              }
            : m,
        ),
      }
    })
    get().refreshSuggestions()
    return Boolean(pending)
  },

  refresh: async () => {
    const state = await fetchSessionState()
    set({
      session: state.session,
      messages: state.messages
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => ({
          id: m.id,
          role: m.role as 'user' | 'assistant',
          content: m.content,
          sources: m.sources,
          toolCalls: m.toolCalls,
          pendingConfirmation: findPendingConfirmation(m.toolCalls),
        })),
    })
  },

  reset: () =>
    set({
      session: null,
      messages: [],
      suggestions: [],
      tour: null,
      avatarState: 'idle',
      errorMessage: null,
      ready: false,
      sitzungsHinweis: null,
      // Der nächste Besucher am Kiosk darf die Karte nicht mit den Daten des
      // vorigen vorfinden.
      lastContact: LEERER_KONTAKT,
      kartenEntwuerfe: {},
    }),

  /** SPEC "Analytics: … Bewertungen". Optimistic: the thumb reacts instantly, a failed write just isn't counted. */
  rateMessage: async (messageId, rating) => {
    set((s) => ({
      messages: s.messages.map((m) => (m.id === messageId ? { ...m, rating } : m)),
    }))
    try {
      await postFeedback(messageId, rating)
    } catch {
      // Rating is a nice-to-have signal, never worth interrupting the conversation over.
    }
  },
}))

/** Der zuletzt besprochene Showcase — aus der letzten Nachricht, die einen trägt. */
function letzterShowcase(messages: UiMessage[]): string | undefined {
  return [...messages].reverse().find((m) => m.showcaseId)?.showcaseId
}

/**
 * Ein Zug im Rundgang: gesprochen als Markierung über ElevenLabs, sonst als
 * Chat-Nachricht mit `entry`. Der Text im Verlauf ist derselbe, den auch der
 * Server für den Zug einträgt.
 */
async function sendeRundgangZug(
  get: () => ChatStore,
  showcaseId: string | null,
  entry: 'tour-walk' | 'tour-arrived' | 'tour-end',
): Promise<void> {
  const t = STRINGS[get().locale]
  const title = showcaseId ? (get().showcases.find((s) => s.id === showcaseId)?.title ?? '') : ''
  const text =
    entry === 'tour-walk'
      ? `${t.tourWalk} ${title}`
      : entry === 'tour-arrived'
        ? `${t.tourArrived} ${title}`
        : t.tourEnd
  const sender = get().voiceMarkerSender
  if (sender) {
    get().addUserTranscript(text, false)
    // Der Zug trägt seinen Showcase — für Kopf und Chips der Antwort.
    useChatStore.setState((s) => ({
      messages: s.messages.map((m, i) =>
        i === s.messages.length - 1 && showcaseId ? { ...m, showcaseId } : m,
      ),
    }))
    sender(
      entry === 'tour-walk'
        ? `[[rundgang:weg:${showcaseId}]]`
        : entry === 'tour-arrived'
          ? `[[rundgang:da:${showcaseId}]]`
          : '[[rundgang:ende]]',
    )
    get().refreshSuggestions()
    return
  }
  await get().sendMessage(text, showcaseId ?? undefined, undefined, undefined, entry)
}

/** Übernimmt die Felder der Kontaktkarte aus den bestätigten Eingaben. */
function kontaktAus(input: Record<string, unknown>): Partial<ContactData> {
  const text = (v: unknown) => (typeof v === 'string' ? v : undefined)
  const teil: Partial<ContactData> = {}
  const name = text(input['name'])
  const company = text(input['company'])
  const phone = text(input['phone'])
  const email = text(input['email'])
  const preferredTime = text(input['preferredTime'])
  if (name !== undefined) teil.name = name
  if (company !== undefined) teil.company = company
  if (phone !== undefined) teil.phone = phone
  if (email !== undefined) teil.email = email
  if (preferredTime !== undefined) teil.preferredTime = preferredTime
  return teil
}

function findPendingConfirmation(
  toolCalls: ToolCallRecord[],
): { toolCallId: string; name: string; input: Record<string, unknown> } | undefined {
  const pending = toolCalls.findLast((c) => c.requiresConfirmation && c.confirmed === undefined)
  return pending ? { toolCallId: pending.id, name: pending.name, input: pending.input } : undefined
}
